import { uid } from '../../../../lib/uid';

/**
 * Test-data generators backing the Autogenerate button on the organization forms.
 *
 * Fills every required field so the primary action enables on the first try. Keep these in
 * lockstep with the forms: a new required field must be added here in the same change, or
 * Autogenerate silently stops working.
 *
 * Values are synthetic. Never put a real organization's name, a real address, or a real
 * contact number in here — this is sample data, not a fixture drawn from production.
 */

const FUNCTIONS = [
  'Operations',
  'Finance',
  'Logistics',
  'Records',
  'Laboratory',
  'Maintenance',
  'Procurement',
  'Training',
];

const TIERS = ['Division', 'Department', 'Section', 'Unit', 'Team', 'Branch'];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Six characters of a uid, which is enough to keep repeated clicks from colliding. */
function suffix(): string {
  return uid().slice(0, 6).toLowerCase();
}

export interface SampleOrgUnit {
  name: string;
  shortCode: string;
}

/**
 * Business-unit mnemonics, for the code a repository reference number is built from.
 *
 * `SYS` is deliberately absent: it is reserved for a document whose encoder resolves to no
 * unit at all, and the server refuses it here — a generator that offered it would produce a
 * form that cannot be saved, which is the one thing Autogenerate exists to prevent.
 */
const SHORT_CODES = ['OPS', 'ADM', 'FIN', 'LOG', 'TECH', 'SVC', 'PLAN', 'AUD'];

/**
 * One unit.
 *
 * The name carries a suffix because a unit's name is unique among its **active siblings**,
 * and Autogenerate is most useful clicked repeatedly under the same parent — without it the
 * second click would produce a name the server rejects as a duplicate.
 *
 * Neither the code, the parent, the tier nor the order is generated. The code follows the
 * parent's own path, the tier defaults to the rung below the parent, the order is the
 * server's to assign, and the parent is either the node the form was opened from or a choice
 * the reader has already made — overwriting any of them would fight the form rather than
 * fill it.
 *
 * The **business unit code** IS generated, unlike the path code beside it: nothing derives it,
 * so there is no suggestion for a generated value to fight, and leaving it empty would make
 * every generated unit unusable as a document's originating unit — which is the surface behind
 * this form that most needs exercising.
 */
export function sampleOrgUnit(): SampleOrgUnit {
  return { name: `${pick(FUNCTIONS)} ${suffix()}`, shortCode: pick(SHORT_CODES) };
}

export interface SampleOrgUnitType {
  name: string;
}

/**
 * One tier of the chart's vocabulary.
 *
 * The name only: a tier's position in the ladder is the server's to assign, so there is
 * nothing else here for a reader to have to fill in.
 */
export function sampleOrgUnitType(): SampleOrgUnitType {
  return { name: `${pick(TIERS)} ${suffix()}` };
}
